// Očekávání vs. realita — preseason předpoklady vs. reálné výkony v nové sezoně.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Scales, TrendUp, TrendDown, ArrowsClockwise, Star } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { TREND_CLS } from "./PreseasonDatabase";

const STATUS = {
    exceeds: { label: "Překonává", cls: "border-fa-mint/60 text-fa-mint bg-fa-mint/10" },
    meets: { label: "Plní", cls: "border-white/25 text-white/70 bg-white/[0.04]" },
    lags: { label: "Zaostává", cls: "border-red-400/50 text-red-300 bg-red-400/10" },
    no_minutes: { label: "Nehrál", cls: "border-white/15 text-white/35" },
};

function SurpriseChips({ title, items, icon, cls }) {
    return (
        <section className={`border p-3 ${cls}`} data-testid={`vsr-${title === "Nad očekávání" ? "over" : "under"}`}>
            <div className="font-mono text-[9px] uppercase tracking-widest flex items-center gap-1.5 mb-1.5">{icon} {title}</div>
            <div className="flex flex-wrap gap-1.5">
                {items.map((p) => (
                    <span key={p.fpl_id} className="font-mono text-[10px] px-2 py-1 border border-white/15 bg-black/30">
                        <b>{p.name}</b> <span className="text-white/45">{p.team_short}</span>{" "}
                        <span className={p.delta >= 0 ? "text-fa-mint" : "text-red-300"}>{p.delta > 0 ? "+" : ""}{p.delta.toFixed(0)}b</span>
                    </span>
                ))}
            </div>
        </section>
    );
}

export default function PreseasonVsReality() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [pos, setPos] = useState("");
    const [club, setClub] = useState("");
    const [status, setStatus] = useState("");
    const [onlyGs, setOnlyGs] = useState(false);
    const [sort, setSort] = useState("delta_desc");

    useEffect(() => {
        track("tool_open", { tool: "preseason_vs_reality" });
        api.get("/preseason-db/vs-reality", { timeout: 45000 }).then((r) => setData(r.data)).catch(() => {});
    }, []);

    const clubs = useMemo(() => {
        const s = new Map();
        (data?.players || []).forEach((p) => p.team && s.set(p.team_short, p.team));
        return [...s.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [data]);

    const rows = useMemo(() => {
        let out = (data?.players || []).filter((p) =>
            (!q || (p.name + " " + p.full_name).toLowerCase().includes(q.toLowerCase())) &&
            (!pos || p.pos === pos) && (!club || p.team_short === club) &&
            (!status || p.status === status) && (!onlyGs || p.gs));
        const cmp = {
            delta_desc: (a, b) => b.delta - a.delta,
            delta_asc: (a, b) => a.delta - b.delta,
            points: (a, b) => b.points - a.points,
            rating: (a, b) => (b.gs?.rating ?? -1) - (a.gs?.rating ?? -1),
            price: (a, b) => b.now_price - a.now_price,
        }[sort];
        return out.sort(cmp).slice(0, 250);
    }, [data, q, pos, club, status, onlyGs, sort]);

    if (!data) {
        return <AppShell><div className="border border-white/10 bg-[#0d0d0d] p-8 font-mono text-[11px] text-white/50 flex items-center gap-2"><ArrowsClockwise size={13} className="animate-spin" /> Počítám očekávání vs. realitu…</div></AppShell>;
    }

    return (
        <AppShell>
            <div className="space-y-5" data-testid="vsr-page">
                <header className="border-2 border-fa-mint/40 bg-gradient-to-br from-fa-mint/10 via-transparent to-transparent p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Scales size={11} weight="fill" /> Sezona 26/27 · GW{data.gw}
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Očekávání vs. realita</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Jak si hráči vedou oproti předpokladům — očekávání vychází z cenové skupiny na pozici (peer průměr bodů),
                        u scoutovaných hráčů navíc preseason rating, trend a minutáž z přípravy.
                    </p>
                    <div className="flex gap-4 mt-3 font-mono text-[10px] text-white/55 flex-wrap">
                        <span><b className="text-fa-mint">{data.players.length}</b> hráčů FPL</span>
                        <span><b className="text-fa-mint">{data.with_gs}</b> se scouting profilem</span>
                        <Link to="/preseason/database" className="text-fa-sky hover:underline">→ Preseason databáze</Link>
                        <Link to="/hlidac" className="text-fa-sky hover:underline">→ Hlídač</Link>
                    </div>
                </header>

                <div className="grid md:grid-cols-2 gap-4">
                    <SurpriseChips title="Nad očekávání" items={data.top_over} icon={<TrendUp size={11} />} cls="border-fa-mint/30 bg-fa-mint/5 text-fa-mint" />
                    <SurpriseChips title="Pod očekávání" items={data.top_under} icon={<TrendDown size={11} />} cls="border-red-400/25 bg-red-400/5 text-red-300" />
                </div>

                <div className="flex items-center gap-2 flex-wrap" data-testid="vsr-filters">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat hráče…" data-testid="vsr-search"
                           className="bg-black/40 border border-white/25 px-3 py-1.5 font-mono text-[10px] text-white focus:outline-none focus:border-fa-mint w-44" />
                    <select value={pos} onChange={(e) => setPos(e.target.value)} data-testid="vsr-filter-pos"
                            className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                        <option value="">Všechny pozice</option>
                        {["GKP", "DEF", "MID", "FWD"].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select value={club} onChange={(e) => setClub(e.target.value)} data-testid="vsr-filter-club"
                            className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                        <option value="">Všechny kluby</option>
                        {clubs.map(([sh, n]) => <option key={sh} value={sh}>{n}</option>)}
                    </select>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="vsr-filter-status"
                            className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                        <option value="">Všechny stavy</option>
                        <option value="exceeds">Překonává</option>
                        <option value="meets">Plní</option>
                        <option value="lags">Zaostává</option>
                        <option value="no_minutes">Nehrál</option>
                    </select>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="vsr-sort"
                            className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                        <option value="delta_desc">Δ nejvyšší</option>
                        <option value="delta_asc">Δ nejnižší</option>
                        <option value="points">Body</option>
                        <option value="rating">Preseason rating</option>
                        <option value="price">Cena</option>
                    </select>
                    <label className="font-mono text-[10px] text-white/60 flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={onlyGs} onChange={(e) => setOnlyGs(e.target.checked)} data-testid="vsr-only-gs" className="accent-[#98F2B3]" />
                        jen scoutovaní
                    </label>
                    <span className="font-mono text-[9px] text-white/35 ml-auto">{rows.length} hráčů</span>
                </div>

                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="vsr-table">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="font-mono text-[8px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                <th className="px-3 py-2">Hráč</th><th className="px-3 py-2">Klub</th><th className="px-3 py-2">Poz</th>
                                <th className="px-3 py-2 text-right">Cena</th><th className="px-3 py-2 text-right">Body</th>
                                <th className="px-3 py-2 text-right">Min</th><th className="px-3 py-2 text-right">Oček.</th>
                                <th className="px-3 py-2 text-right">Δ</th><th className="px-3 py-2">Stav</th>
                                <th className="px-3 py-2">Preseason</th><th className="px-3 py-2 text-right">Minutáž příp.→teď</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => {
                                const st = STATUS[p.status];
                                return (
                                    <tr key={p.fpl_id} className="border-b border-white/5 hover:bg-white/[0.02] font-mono text-[10px]" data-testid={`vsr-row-${p.fpl_id}`}>
                                        <td className="px-3 py-1.5 font-display font-bold text-[12px] whitespace-nowrap">
                                            {p.name}{p.gs?.shortlist && <Star size={10} weight="fill" className="inline ml-1 text-fa-yellow" />}
                                        </td>
                                        <td className="px-3 py-1.5 text-white/55">{p.team_short}</td>
                                        <td className="px-3 py-1.5 text-white/55">{p.pos}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-white/80">
                                            £{p.now_price.toFixed(1)}{p.now_price !== p.start_price && (
                                                <span className={p.now_price > p.start_price ? "text-fa-mint" : "text-red-300"}> {p.now_price > p.start_price ? "▲" : "▼"}</span>)}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums font-bold text-white/90">{p.points}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-white/60">{p.minutes}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-white/50">{p.expected.toFixed(1)}</td>
                                        <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${p.delta > 0 ? "text-fa-mint" : p.delta < 0 ? "text-red-300" : "text-white/50"}`}>
                                            {p.delta > 0 ? "+" : ""}{p.delta.toFixed(1)}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <span className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                                        </td>
                                        <td className="px-3 py-1.5 whitespace-nowrap">
                                            {p.gs ? (
                                                <span>
                                                    {p.gs.rating != null && <b className="text-fa-sky">{p.gs.rating}/10</b>}
                                                    {p.gs.trend && <span className={`ml-1.5 ${TREND_CLS(p.gs.trend)}`}>{p.gs.trend}</span>}
                                                    {p.gs.rotation_risk && <span className="ml-1.5 text-white/40 text-[9px]">rot. {p.gs.rotation_risk}</span>}
                                                </span>
                                            ) : <span className="text-white/25">—</span>}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-white/60">
                                            {p.prep_share != null ? `${Math.round(p.prep_share)}%` : "—"} → {Math.round(p.minutes_share)}%
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </AppShell>
    );
}
